(() => {
  if (window.__hanmiaVariantSelectFixLoaded) return;
  window.__hanmiaVariantSelectFixLoaded = true;

  const selectedOption = (select) => select?.selectedOptions?.[0] || null;

  const setSelectedAttribute = (select) => {
    const current = selectedOption(select);
    if (!current) return;

    Array.from(select.options).forEach((option) => {
      if (option === current) {
        option.setAttribute('selected', 'selected');
      } else {
        option.removeAttribute('selected');
      }
    });
  };

  const syncSelectToSwatch = (select) => {
    const row = select.closest('.product-option-row');
    if (!row) return;

    const current = selectedOption(select);
    const optionValueId = current?.dataset?.optionValueId;
    if (!optionValueId) return;

    row.querySelectorAll('input[type="radio"][data-option-value-id]').forEach((radio) => {
      radio.checked = radio.dataset.optionValueId === optionValueId;
    });
  };

  const syncSwatchToSelect = (radio) => {
    const row = radio.closest('.product-option-row');
    if (!row) return;

    const select = row.querySelector('select');
    const optionValueId = radio.dataset.optionValueId;
    if (!select || !optionValueId) return;

    const match = Array.from(select.options).find(
      (option) => option.dataset.optionValueId === optionValueId
    );
    if (!match) return;

    select.value = match.value;
    Array.from(select.options).forEach((option) => {
      if (option === match) {
        option.setAttribute('selected', 'selected');
      } else {
        option.removeAttribute('selected');
      }
    });
  };

  // Keep the visible dropdown and swatches in sync without dispatching a second
  // synthetic change event. Dawn receives one real change event per customer action.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('variant-selects')) return;

      if (target.tagName === 'SELECT') {
        setSelectedAttribute(target);
        syncSelectToSwatch(target);
        return;
      }

      if (
        target.matches('input[type="radio"][data-option-value-id]') &&
        target.closest('.product-option-row')
      ) {
        syncSwatchToSelect(target);
      }
    },
    true
  );

  customElements.whenDefined('variant-selects').then(() => {
    const VariantSelectsElement = customElements.get('variant-selects');
    if (!VariantSelectsElement) return;

    const proto = VariantSelectsElement.prototype;
    if (proto.__hanmiaVariantSelectionFix) return;
    proto.__hanmiaVariantSelectionFix = true;

    // Read the browser's actual selected option properties rather than relying
    // on literal selected attributes. This is more reliable for native mobile
    // selects, while still de-duplicating Hanmia's swatch + dropdown controls.
    Object.defineProperty(proto, 'selectedOptionValues', {
      configurable: true,
      get() {
        const selectedSelectOptions = Array.from(this.querySelectorAll('select'))
          .map((select) => selectedOption(select))
          .filter(Boolean);
        const checkedInputs = Array.from(this.querySelectorAll('fieldset input:checked'));

        return [...new Set(
          [...selectedSelectOptions, ...checkedInputs]
            .map(({ dataset }) => dataset.optionValueId)
            .filter(Boolean)
        )];
      },
    });

    // Dawn's default SELECT metadata update assumes an option with a literal
    // selected attribute is always present. iOS can update the selected property
    // first, so normalise the attribute ourselves and handle the optional swatch
    // preview without using the fragile querySelector('[selected]') path.
    const originalUpdateSelectionMetadata = proto.updateSelectionMetadata;
    proto.updateSelectionMetadata = function updateSelectionMetadataSafely(event) {
      const target = event?.target;

      if (target?.tagName === 'SELECT') {
        const current = selectedOption(target);
        setSelectedAttribute(target);

        const swatchValue = target
          .closest('.product-form__input')
          ?.querySelector('[data-selected-value] > .swatch');

        if (swatchValue) {
          const selectedDropdownSwatchValue = current?.getAttribute('data-option-swatch-value');

          if (selectedDropdownSwatchValue) {
            swatchValue.style.setProperty('--swatch--background', selectedDropdownSwatchValue);
            swatchValue.classList.remove('swatch--unavailable');
          } else {
            swatchValue.style.setProperty('--swatch--background', 'unset');
            swatchValue.classList.add('swatch--unavailable');
          }
        }
        return;
      }

      return originalUpdateSelectionMetadata.call(this, event);
    };
  });

  customElements.whenDefined('product-info').then(() => {
    const ProductInfoElement = customElements.get('product-info');
    if (!ProductInfoElement) return;

    const proto = ProductInfoElement.prototype;
    if (proto.__hanmiaVariantRequestFix) return;
    proto.__hanmiaVariantRequestFix = true;

    const originalBuildRequestUrlWithParams = proto.buildRequestUrlWithParams;
    proto.buildRequestUrlWithParams = function buildRequestUrlWithUniqueOptions(
      url,
      optionValues = [],
      shouldFetchFullPage = false
    ) {
      const uniqueOptionValues = [...new Set(optionValues.filter(Boolean))];
      return originalBuildRequestUrlWithParams.call(
        this,
        url,
        uniqueOptionValues,
        shouldFetchFullPage
      );
    };
  });

  // The Liquid counter is based on total product media, but variant filtering
  // can leave only one image visible. Re-evaluate the mobile media controls from
  // the slides that are actually visible for the selected variant.
  customElements.whenDefined('slider-component').then(() => {
    const SliderComponentElement = customElements.get('slider-component');
    if (!SliderComponentElement) return;

    const proto = SliderComponentElement.prototype;
    if (proto.__hanmiaSingleMediaControlsFix) return;
    proto.__hanmiaSingleMediaControlsFix = true;

    const originalInitPages = proto.initPages;
    proto.initPages = function initPagesWithVisibleMediaCount() {
      originalInitPages.call(this);

      const isProductMediaViewer =
        this.id?.startsWith('GalleryViewer-') ||
        this.slider?.id?.startsWith('Slider-Gallery-');
      if (!isProductMediaViewer) return;

      const visibleItems = Array.from(this.sliderItems || []).filter(
        (item) => item.clientWidth > 0
      );
      const controls = this.querySelector('.slider-buttons');
      if (!controls) return;

      const singleMedia = visibleItems.length <= 1;
      controls.hidden = singleMedia;
      controls.style.display = singleMedia ? 'none' : '';

      if (singleMedia) {
        const current = controls.querySelector('.slider-counter--current');
        const total = controls.querySelector('.slider-counter--total');
        if (current) current.textContent = visibleItems.length ? '1' : '0';
        if (total) total.textContent = String(visibleItems.length);
      }
    };

    // Apply the rule immediately to an already-connected gallery as well as on
    // every later reset triggered by a variant change.
    document
      .querySelectorAll('slider-component[id^="GalleryViewer-"]')
      .forEach((slider) => slider.initPages?.());
  });
})();
